import type { Permission } from '@talentbabu/types';
import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { GuestOnly, RequireAuth, RequirePermission } from '../features/auth/guards';
import { NotFoundPage, RouteErrorPage } from '../features/errors/ErrorPages';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { ADMIN_NAV, CANDIDATE_NAV, EMPLOYER_NAV, RECRUITER_NAV } from '../layouts/nav';
import { PublicLayout } from '../layouts/PublicLayout';

/** Code-split route: the page module's default export becomes the route component. */
function page(load: () => Promise<{ default: ComponentType }>, permission?: Permission): Pick<RouteObject, 'lazy'> {
  return {
    lazy: async () => {
      const { default: Page } = await load();
      return {
        Component: permission
          ? () => (
              <RequirePermission permission={permission}>
                <Page />
              </RequirePermission>
            )
          : Page,
      };
    },
  };
}

const pages = {
  candidateDetail: () =>
    import('../features/details/pages/EntityPages').then((m) => ({ default: m.CandidateDetailPage })),
  companyDetail: () => import('../features/details/pages/EntityPages').then((m) => ({ default: m.CompanyDetailPage })),
  userDetail: () => import('../features/details/pages/EntityPages').then((m) => ({ default: m.UserDetailPage })),
  jobDetail: () => import('../features/details/pages/WorkflowPages').then((m) => ({ default: m.JobManageDetailPage })),
  placementDetail: () =>
    import('../features/details/pages/WorkflowPages').then((m) => ({ default: m.PlacementDetailPage })),
  interviewDetail: () =>
    import('../features/details/pages/WorkflowPages').then((m) => ({ default: m.InterviewDetailPage })),
  leadDetail: () => import('../features/details/pages/WorkflowPages').then((m) => ({ default: m.LeadDetailPage })),
  paymentDetail: () =>
    import('../features/details/pages/WorkflowPages').then((m) => ({ default: m.PaymentDetailPage })),
  invoiceDetail: () =>
    import('../features/details/pages/WorkflowPages').then((m) => ({ default: m.InvoiceDetailPage })),
  myApplicationDetail: () =>
    import('../features/details/pages/WorkflowPages').then((m) => ({ default: m.MyApplicationDetailPage })),
  account: () => import('../features/account/AccountSettingsPage'),
  notifications: () => import('../features/notifications/NotificationsPage'),
  manageJobs: () => import('../features/jobs/pages/ManageJobsPage'),
  jobEditor: () => import('../features/jobs/pages/JobEditorPage'),
  pipeline: () => import('../features/applications/pages/ApplicationsPipelinePage'),
  applicationDetail: () => import('../features/applications/pages/ApplicationDetailPage'),
  companies: () => import('../features/admin/pages/AdminCompaniesPage'),
  candidates: () => import('../features/admin/pages/AdminCandidatesPage'),
  requirements: () => import('../features/crm/pages/RequirementsPage'),
  requirementDetail: () => import('../features/crm/pages/RequirementDetailPage'),
  leads: () => import('../features/crm/pages/LeadsPage'),
  tasks: () => import('../features/crm/pages/TasksPage'),
  interviews: () => import('../features/hiring/pages/InterviewsPage'),
  hires: () => import('../features/hiring/pages/HiresPage'),
  myPerformance: () =>
    import('../features/crm/pages/RecruiterPerformancePage').then((m) => ({ default: m.MyPerformancePage })),
  talentSearch: () => import('../features/talent/pages/TalentSearchPage'),
  talentProfile: () => import('../features/talent/pages/TalentProfilePage'),
  shortlists: () => import('../features/talent/pages/ShortlistsPage'),
  shortlistDetail: () =>
    import('../features/talent/pages/ShortlistsPage').then((m) => ({ default: m.ShortlistDetailPage })),
  candidateVerification: () =>
    import('../features/talent/pages/VerificationPages').then((m) => ({ default: m.CandidateVerificationPage })),
  verificationQueue: () =>
    import('../features/talent/pages/VerificationPages').then((m) => ({ default: m.VerificationQueuePage })),
  candidateAssessments: () =>
    import('../features/talent/pages/AssessmentPages').then((m) => ({ default: m.CandidateAssessmentsPage })),
  attempt: () => import('../features/talent/pages/AssessmentPages').then((m) => ({ default: m.AttemptPage })),
  adminAssessments: () =>
    import('../features/talent/pages/AdminAssessmentPages').then((m) => ({ default: m.AdminAssessmentsPage })),
  assessmentEditor: () =>
    import('../features/talent/pages/AdminAssessmentPages').then((m) => ({ default: m.AssessmentEditorPage })),
  candidateInsights: () =>
    import('../features/analytics/pages/AnalyticsPages').then((m) => ({ default: m.CandidateInsightsPage })),
  adminAnalytics: () =>
    import('../features/analytics/pages/AnalyticsPages').then((m) => ({ default: m.AdminAnalyticsPage })),
  reports: () => import('../features/analytics/pages/AnalyticsPages').then((m) => ({ default: m.ReportsPage })),
  supportList: () => import('../features/content/pages/SupportPages').then((m) => ({ default: m.SupportListPage })),
  supportTicket: () => import('../features/content/pages/SupportPages').then((m) => ({ default: m.SupportTicketPage })),
  leaderboard: () =>
    import('../features/crm/pages/RecruiterPerformancePage').then((m) => ({ default: m.RecruiterLeaderboardPage })),
};

/** Job + application management screens shared by employers, recruiters and admins. */
function hiringRoutes(listPermission: Permission): RouteObject[] {
  return [
    { path: 'jobs', ...page(pages.manageJobs, listPermission) },
    { path: 'jobs/create', ...page(pages.jobEditor, 'job.create') },
    { path: 'jobs/:id/edit', ...page(pages.jobEditor, 'job.edit') },
    { path: 'jobs/:id', ...page(pages.jobDetail, listPermission) },
    { path: 'applications', ...page(pages.pipeline, 'application.view') },
    { path: 'applications/:id', ...page(pages.applicationDetail, 'application.view') },
    { path: 'requirements', ...page(pages.requirements, 'requirement.view') },
    { path: 'requirements/:id', ...page(pages.requirementDetail, 'requirement.view') },
    { path: 'interviews', ...page(pages.interviews, 'interview.manage') },
    { path: 'interviews/:id', ...page(pages.interviewDetail, 'interview.manage') },
  ];
}

function area(
  path: string,
  roles: Parameters<typeof RequireAuth>[0]['roles'],
  nav: typeof ADMIN_NAV,
  children: RouteObject[],
): RouteObject {
  return {
    path,
    element: <RequireAuth roles={roles} />,
    children: [
      {
        element: <DashboardLayout nav={nav} />,
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          ...children,
          { path: 'notifications', ...page(pages.notifications) },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  };
}

export const routes: RouteObject[] = [
  {
    element: <PublicLayout />,
    children: [
      { index: true, ...page(() => import('../features/home/HomePage')) },
      { path: 'jobs', ...page(() => import('../features/jobs/pages/JobSearchPage')) },
      { path: 'jobs/:slug', ...page(() => import('../features/jobs/pages/JobSlugPage')) },
      {
        path: 'companies',
        ...page(() => import('../features/content/pages/PublicPages').then((m) => ({ default: m.CompaniesPage }))),
      },
      {
        path: 'companies/:slug',
        ...page(() => import('../features/content/pages/PublicPages').then((m) => ({ default: m.CompanyProfilePage }))),
      },
      {
        path: 'blog',
        ...page(() => import('../features/content/pages/PublicPages').then((m) => ({ default: m.BlogListPage }))),
      },
      {
        path: 'blog/:slug',
        ...page(() => import('../features/content/pages/PublicPages').then((m) => ({ default: m.BlogPostPage }))),
      },
      {
        path: 'about',
        ...page(() => import('../features/content/pages/PublicPages').then((m) => ({ default: m.AboutPage }))),
      },
      {
        path: 'services',
        ...page(() => import('../features/content/pages/PublicPages').then((m) => ({ default: m.ServicesPage }))),
      },
      {
        path: 'contact',
        ...page(() => import('../features/content/pages/PublicPages').then((m) => ({ default: m.ContactPage }))),
      },
      { path: 'top-companies', ...page(() => import('../features/content/pages/ShowcasePage')) },
      { path: 'job-roles', ...page(() => import('../features/content/pages/JobRolesPage')) },
      { path: 'pricing', ...page(() => import('../features/billing/pages/PricingPage')) },
      { path: 'image-credits', ...page(() => import('../features/home/ImageCreditsPage')) },
      {
        path: 'terms',
        ...page(() => import('../features/legal/LegalPages').then((m) => ({ default: m.TermsPage }))),
      },
      {
        path: 'privacy',
        ...page(() => import('../features/legal/LegalPages').then((m) => ({ default: m.PrivacyPage }))),
      },
      {
        path: 'refund-policy',
        ...page(() => import('../features/legal/LegalPages').then((m) => ({ default: m.RefundPolicyPage }))),
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    element: <GuestOnly />,
    children: [
      { path: 'login', ...page(() => import('../features/auth/pages/LoginPage')) },
      {
        path: 'employer/login',
        ...page(() => import('../features/auth/pages/LoginPage').then((m) => ({ default: m.EmployerLoginPage }))),
      },
      {
        path: 'admin/login',
        ...page(() => import('../features/auth/pages/LoginPage').then((m) => ({ default: m.AdminLoginPage }))),
      },
      { path: 'register', ...page(() => import('../features/auth/pages/RegisterPage')) },
      {
        path: 'employer/register',
        ...page(() => import('../features/auth/pages/RegisterPage').then((m) => ({ default: m.EmployerRegisterPage }))),
      },
      {
        path: 'forgot-password',
        ...page(() => import('../features/auth/pages/PasswordPages').then((m) => ({ default: m.ForgotPasswordPage }))),
      },
    ],
  },
  {
    path: 'reset-password',
    ...page(() => import('../features/auth/pages/PasswordPages').then((m) => ({ default: m.ResetPasswordPage }))),
  },
  {
    path: 'verify-email',
    ...page(() => import('../features/auth/pages/PasswordPages').then((m) => ({ default: m.VerifyEmailPage }))),
  },
  area('candidate', ['CANDIDATE'], CANDIDATE_NAV, [
    { path: 'dashboard', ...page(() => import('../features/candidate/pages/CandidateDashboardPage')) },
    {
      path: 'jobs',
      ...page(() =>
        import('../features/jobs/pages/JobSearchPage').then((m) => ({ default: () => <m.default embedded /> })),
      ),
    },
    { path: 'applications', ...page(() => import('../features/applications/pages/MyApplicationsPage')) },
    { path: 'applications/:id', ...page(pages.myApplicationDetail) },
    { path: 'interviews/:id', ...page(pages.interviewDetail) },
    { path: 'payments/:id', ...page(pages.paymentDetail) },
    { path: 'saved-jobs', ...page(() => import('../features/candidate/pages/SavedJobsPage')) },
    { path: 'recommended-jobs', ...page(() => import('../features/candidate/pages/RecommendedJobsPage')) },
    { path: 'invites', ...page(() => import('../features/candidate/pages/InvitesPage')) },
    { path: 'profile', ...page(() => import('../features/candidate/pages/CandidateProfilePage')) },
    { path: 'resume', ...page(() => import('../features/candidate/pages/ResumesPage')) },
    { path: 'interviews', ...page(() => import('../features/hiring/pages/CandidateInterviewsPage')) },
    { path: 'payments', ...page(() => import('../features/billing/pages/CandidatePaymentsPage')) },
    { path: 'verification', ...page(pages.candidateVerification) },
    { path: 'insights', ...page(pages.candidateInsights) },
    {
      path: 'referrals',
      ...page(() =>
        import('../features/content/pages/ContentAdminPages').then((m) => ({ default: m.CandidateReferralsPage })),
      ),
    },
    { path: 'support', ...page(pages.supportList) },
    { path: 'support/:id', ...page(pages.supportTicket) },
    { path: 'assessments', ...page(pages.candidateAssessments) },
    { path: 'assessments/attempt/:id', ...page(pages.attempt) },
    { path: 'settings', ...page(pages.account) },
  ]),
  area('employer', ['EMPLOYER', 'HR_MANAGER'], EMPLOYER_NAV, [
    { path: 'dashboard', ...page(() => import('../features/employer/pages/EmployerDashboardPage')) },
    { path: 'company', ...page(() => import('../features/employer/pages/EmployerCompanyPage')) },
    ...hiringRoutes('job.create'),
    { path: 'hires', ...page(pages.hires, 'offer.manage') },
    { path: 'placements/:id', ...page(pages.placementDetail, 'offer.manage') },
    { path: 'billing/payments/:id', ...page(pages.paymentDetail, 'payment.view') },
    { path: 'billing/invoices/:id', ...page(pages.invoiceDetail, 'subscription.view') },
    { path: 'billing', ...page(() => import('../features/billing/pages/EmployerBillingPage'), 'subscription.view') },
    { path: 'talent', ...page(pages.talentSearch, 'candidate.view') },
    {
      path: 'insights',
      ...page(
        () => import('../features/analytics/pages/AnalyticsPages').then((m) => ({ default: m.EmployerAnalyticsPage })),
        'application.view',
      ),
    },
    { path: 'support', ...page(pages.supportList) },
    { path: 'support/:id', ...page(pages.supportTicket) },
    { path: 'talent/:id', ...page(pages.talentProfile, 'candidate.view') },
    { path: 'shortlists', ...page(pages.shortlists, 'candidate.view') },
    { path: 'shortlists/:id', ...page(pages.shortlistDetail, 'candidate.view') },
    { path: 'settings', ...page(pages.account) },
  ]),
  area('recruiter', ['RECRUITER'], RECRUITER_NAV, [
    { path: 'dashboard', ...page(() => import('../features/recruiter/pages/RecruiterDashboardPage')) },
    ...hiringRoutes('job.create'),
    { path: 'leads', ...page(pages.leads, 'crm.view') },
    { path: 'tasks', ...page(pages.tasks, 'task.view') },
    { path: 'placements', ...page(pages.hires, 'offer.manage') },
    { path: 'placements/:id', ...page(pages.placementDetail, 'offer.manage') },
    { path: 'leads/:id', ...page(pages.leadDetail, 'crm.view') },
    { path: 'candidates/:id', ...page(pages.candidateDetail, 'candidate.view') },
    { path: 'companies/:id', ...page(pages.companyDetail, 'company.view') },
    { path: 'analytics', ...page(pages.myPerformance) },
    { path: 'verification', ...page(pages.verificationQueue, 'verification.manage') },
    { path: 'candidates', ...page(pages.candidates, 'candidate.view') },
    { path: 'companies', ...page(pages.companies, 'company.view') },
    { path: 'settings', ...page(pages.account) },
  ]),
  area('admin', ['SUPER_ADMIN', 'ADMIN', 'FINANCE', 'SUPPORT'], ADMIN_NAV, [
    { path: 'dashboard', ...page(() => import('../features/admin/pages/AdminDashboardPage')) },
    ...hiringRoutes('job.approve'),
    { path: 'leads', ...page(pages.leads, 'crm.view') },
    { path: 'tasks', ...page(pages.tasks, 'task.view') },
    { path: 'placements', ...page(pages.hires, 'placement.manage') },
    { path: 'placements/:id', ...page(pages.placementDetail, 'placement.manage') },
    { path: 'leads/:id', ...page(pages.leadDetail, 'crm.view') },
    { path: 'candidates/:id', ...page(pages.candidateDetail, 'candidate.view') },
    { path: 'companies/:id', ...page(pages.companyDetail, 'company.view') },
    { path: 'users/:id', ...page(pages.userDetail, 'user.manage') },
    { path: 'finance/payments/:id', ...page(pages.paymentDetail, 'payment.view') },
    { path: 'finance/invoices/:id', ...page(pages.invoiceDetail, 'payment.view') },
    { path: 'recruiters', ...page(pages.leaderboard, 'recruiter.view') },
    { path: 'candidates', ...page(pages.candidates, 'candidate.view') },
    { path: 'companies', ...page(pages.companies, 'company.view') },
    { path: 'users', ...page(() => import('../features/admin/pages/AdminUsersPage'), 'user.manage') },
    { path: 'settings', ...page(() => import('../features/admin/pages/AdminSettingsPage'), 'settings.manage') },
    { path: 'finance', ...page(() => import('../features/billing/pages/AdminFinancePage'), 'payment.view') },
    { path: 'plans', ...page(() => import('../features/billing/pages/AdminPlansPage'), 'plan.manage') },
    { path: 'analytics', ...page(pages.adminAnalytics) },
    { path: 'reports', ...page(pages.reports, 'report.view') },
    { path: 'verification', ...page(pages.verificationQueue, 'verification.manage') },
    { path: 'assessments', ...page(pages.adminAssessments, 'assessment.manage') },
    { path: 'assessments/:id', ...page(pages.assessmentEditor, 'assessment.manage') },
    {
      path: 'messaging',
      ...page(() => import('../features/messaging/pages/AdminMessagingPage'), 'notification.manage'),
    },
    { path: 'audit-logs', ...page(() => import('../features/admin/pages/AdminAuditLogsPage'), 'audit.view') },
    { path: 'support', ...page(pages.supportList, 'support.view') },
    { path: 'support/:id', ...page(pages.supportTicket, 'support.view') },
    {
      path: 'referrals',
      ...page(
        () => import('../features/content/pages/ContentAdminPages').then((m) => ({ default: m.AdminReferralsPage })),
        'payment.view',
      ),
    },
    { path: 'content', ...page(() => import('../features/content/pages/AdminContentPage'), 'content.manage') },
    {
      path: 'blog',
      ...page(
        () => import('../features/content/pages/ContentAdminPages').then((m) => ({ default: m.AdminBlogPage })),
        'blog.manage',
      ),
    },
    {
      path: 'blog/:id',
      ...page(
        () => import('../features/content/pages/ContentAdminPages').then((m) => ({ default: m.BlogEditorPage })),
        'blog.manage',
      ),
    },
    {
      path: 'seo',
      ...page(
        () => import('../features/content/pages/ContentAdminPages').then((m) => ({ default: m.AdminSeoPage })),
        'seo.manage',
      ),
    },
    { path: 'payment-logs', ...page(() => import('../features/billing/pages/AdminPaymentLogsPage'), 'content.manage') },
    { path: 'account', ...page(pages.account) },
  ]),
];

export function createAppRouter() {
  // One boundary above every layout, so a crash anywhere shows a recoverable page.
  return createBrowserRouter([{ errorElement: <RouteErrorPage />, children: routes }]);
}
